import React, {
  useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo,
} from 'react';
import classNames from 'classnames';
import { useSize } from 'ahooks';
import styles from './index.module.scss';

export interface WaterfallProps<T = any> {
  /** 数据列表 */
  items: T[];
  /** 渲染每个项目的函数 */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** 获取每个项目的唯一 key */
  getItemKey?: (item: T, index: number) => string | number;
  /** 获取每个项目的预估高度（用于初始分配，避免占位数据都分配到第一列） */
  getEstimatedHeight?: (item: T, index: number, itemWidth: number) => number;
  /** 列的最小宽度（px） */
  minColumnWidth?: number;
  /** 列的最大宽度（px） */
  maxColumnWidth?: number;
  /** 列间距（px） */
  columnGap?: number;
  /** 行间距（px） */
  rowGap?: number;
  /** 固定列数（如果设置，将忽略 minColumnWidth 和 maxColumnWidth） */
  columns?: number;
  /** 容器类名 */
  className?: string;
  /** 容器样式 */
  style?: React.CSSProperties;
  /** 是否启用虚拟滚动（适用于大量数据） */
  virtualized?: boolean;
  /** 响应式断点配置 */
  breakpoints?: {
    [key: number]: number; // 容器宽度 => 列数
  };
}

interface ColumnItem<T> {
  item: T;
  index: number;
  height: number;
}

/** 基于容器宽度和卡片实际高度分配瀑布流列。 */
function Waterfall<T = any>(props: WaterfallProps<T>) {
  const {
    items,
    renderItem,
    getItemKey = (_, index) => index,
    getEstimatedHeight,
    minColumnWidth = 280,
    maxColumnWidth = 400,
    columnGap = 16,
    rowGap = 16,
    columns: fixedColumns,
    className,
    style,
    virtualized = false,
    breakpoints,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement[]>([]);
  const itemHeightsRef = useRef<Map<string | number, number>>(new Map());
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedElementsRef = useRef<Set<HTMLElement>>(new Set());
  const resizeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const distributeItemsRef = useRef<() => void>();
  const [columns, setColumns] = useState<number>(1);
  const [columnItems, setColumnItems] = useState<ColumnItem<T>[][]>([]);

  const size = useSize(containerRef);

  // 使用 useMemo 缓存断点配置，避免对象引用变化导致重新计算
  const sortedBreakpoints = useMemo(() => {
    if (!breakpoints) return null;
    return Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => b - a);
  }, [breakpoints]);

  // 计算列数
  const calculateColumns = useCallback((width: number): number => {
    if (fixedColumns) {
      return fixedColumns;
    }

    // 如果有断点配置，使用断点
    if (sortedBreakpoints && breakpoints) {
      for (const breakpoint of sortedBreakpoints) {
        if (width >= breakpoint) {
          return breakpoints[breakpoint];
        }
      }
    }

    // 基于最小宽度计算列数
    let cols = Math.max(1, Math.floor((width + columnGap) / (minColumnWidth + columnGap)));

    // 计算实际列宽
    let itemWidth = Math.floor((width - columnGap * (cols - 1)) / cols);

    // 如果列宽超过最大宽度，尝试增加列数
    while (itemWidth > maxColumnWidth && cols < 20) {
      const nextCols = cols + 1;
      const nextItemWidth = Math.floor((width - columnGap * (nextCols - 1)) / nextCols);
      if (nextItemWidth < minColumnWidth) break;
      cols = nextCols;
      itemWidth = nextItemWidth;
    }

    return cols;
  }, [fixedColumns, minColumnWidth, maxColumnWidth, columnGap, sortedBreakpoints, breakpoints]);

  // 更新列数
  useEffect(() => {
    // 优先使用容器实际宽度，其次使用 size.width
    const containerWidth = containerRef.current?.clientWidth || size?.width;
    if (!containerWidth) return;

    const newColumns = calculateColumns(containerWidth);
    if (newColumns !== columns) {
      setColumns(newColumns);
    }
  }, [size?.width, calculateColumns, columns]);

  // 分配项目到各列
  // 优化：立即使用缓存高度或预估高度计算布局，避免闪烁
  const distributeItems = useCallback(() => {
    if (columns === 0 || items.length === 0) {
      setColumnItems([]);
      return;
    }

    const newColumnItems: ColumnItem<T>[][] = Array.from({ length: columns }, () => []);
    const columnHeights = new Array(columns).fill(0);

    // 计算每个 item 的宽度
    // 优先使用容器实际宽度，如果不可用则使用 size.width，最后使用 minColumnWidth
    const containerWidth = containerRef.current?.clientWidth
      || size?.width
      || (typeof window !== 'undefined' ? window.innerWidth : 0);
    const itemWidth = containerWidth > 0
      ? Math.floor((containerWidth - columnGap * (columns - 1)) / columns)
      : minColumnWidth;

    items.forEach((item, index) => {
      // 找到高度最小的列
      const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));

      const key = getItemKey(item, index);
      const cachedHeight = itemHeightsRef.current.get(key);

      // 优先使用缓存高度，其次使用预估高度，最后使用默认高度
      let height = cachedHeight;
      if (!height && getEstimatedHeight) {
        height = getEstimatedHeight(item, index, itemWidth);
      }
      // 如果没有预估高度，使用一个合理的默认值，避免所有项都分配到第一列
      if (!height) {
        height = itemWidth * 1.2; // 默认宽高比 1:1.2
      }

      newColumnItems[minHeightIndex].push({
        item,
        index,
        height,
      });

      // 更新列高度
      columnHeights[minHeightIndex] += height + rowGap;
    });

    setColumnItems(newColumnItems);
  }, [items, columns, rowGap, getItemKey, getEstimatedHeight, columnGap, minColumnWidth, size?.width]);

  // 使用 ref 保存最新的 distributeItems，供 ResizeObserver 回调使用
  useEffect(() => {
    distributeItemsRef.current = distributeItems;
  }, [distributeItems]);

  // 当列数或数据变化时立即重新分配（使用 useLayoutEffect 同步执行，避免闪烁）
  useLayoutEffect(() => {
    // 如果容器宽度不可用，等待下一帧
    if (!containerRef.current?.clientWidth && !size?.width) {
      requestAnimationFrame(() => {
        distributeItems();
      });
    } else {
      distributeItems();
    }
  }, [distributeItems, size?.width]);

  // 当 columnItems 更新后，观察新元素
  useEffect(() => {
    if (virtualized || !observerRef.current) {
      return;
    }

    // 使用 requestAnimationFrame 确保在 DOM 更新后观察
    let timeoutId: NodeJS.Timeout | undefined;
    const rafId = requestAnimationFrame(() => {
      // 再延迟一点，确保 DOM 完全渲染
      timeoutId = setTimeout(() => {
        const observer = observerRef.current;
        if (!observer) return;

        const newElements: HTMLElement[] = [];

        // 先清理不再存在的元素
        const currentKeys = new Set<string | number>();
        columnsRef.current.forEach((column) => {
          if (column) {
            const columnItemElements = column.querySelectorAll('[data-item-key]');
            columnItemElements.forEach((item) => {
              const key = (item as HTMLElement).dataset.itemKey;
              if (key) currentKeys.add(key);
            });
          }
        });

        // 移除不再存在的元素的观察
        observedElementsRef.current.forEach((element) => {
          const key = element.dataset.itemKey;
          if (key && !currentKeys.has(key)) {
            observer.unobserve(element);
            observedElementsRef.current.delete(element);
          }
        });

        // 观察新元素
        columnsRef.current.forEach((column) => {
          if (column) {
            const columnItemElements = column.querySelectorAll('[data-item-key]');
            columnItemElements.forEach((item) => {
              const element = item as HTMLElement;
              const key = element.dataset.itemKey;

              // 只观察尚未观察的元素
              if (key && !observedElementsRef.current.has(element)) {
                // 如果高度还未缓存，先尝试获取一次
                if (!itemHeightsRef.current.has(key) && element.offsetHeight > 0) {
                  itemHeightsRef.current.set(key, element.offsetHeight);
                }
                observer.observe(element);
                observedElementsRef.current.add(element);
                newElements.push(element);
              }
            });
          }
        });

        // 如果有新元素被观察且高度已缓存，可能需要重新分配
        if (newElements.length > 0) {
          // 检查是否有新元素的高度被缓存了
          const hasNewHeight = newElements.some((element) => {
            const key = element.dataset.itemKey;
            return key && itemHeightsRef.current.has(key);
          });

          if (hasNewHeight && distributeItemsRef.current) {
            // 延迟执行，等待所有元素渲染完成
            setTimeout(() => {
              if (distributeItemsRef.current) {
                distributeItemsRef.current();
              }
            }, 50);
          }
        }
      }, 100);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [columnItems, virtualized]); // 依赖 columnItems，当它更新时观察新元素

  // 监听项目高度变化
  // 关键修复：移除 columnItems 依赖，避免无限循环
  // 只在初始化时创建一次 ResizeObserver，然后动态添加/移除观察的元素
  useEffect(() => {
    if (virtualized) {
      return undefined; // 虚拟滚动模式下不监听高度
    }

    // 创建或复用 ResizeObserver
    if (typeof ResizeObserver === 'undefined') return undefined;
    if (!observerRef.current) {
      observerRef.current = new ResizeObserver((entries) => {
        let hasChange = false;

        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          const key = element.dataset.itemKey;
          if (key) {
            const newHeight = entry.contentRect.height;
            const oldHeight = itemHeightsRef.current.get(key);

            // 只有当高度真正变化且大于 0 时才更新
            if (newHeight > 0 && oldHeight !== newHeight) {
              itemHeightsRef.current.set(key, newHeight);
              hasChange = true;
            }
          }
        });

        if (hasChange) {
          // 清除之前的定时器
          if (resizeTimerRef.current) {
            clearTimeout(resizeTimerRef.current);
          }
          // 延迟重新分配，避免频繁更新
          // 使用 ref 获取最新的 distributeItems
          resizeTimerRef.current = setTimeout(() => {
            if (distributeItemsRef.current) {
              distributeItemsRef.current();
            }
          }, 100); // 增加延迟时间，减少更新频率
        }
      });
    }

    const observer = observerRef.current;

    // 延迟观察新元素，确保 DOM 已经渲染完成
    const timeoutId = setTimeout(() => {
      const newElements: HTMLElement[] = [];

      // 先清理不再存在的元素
      const currentKeys = new Set<string | number>();
      columnsRef.current.forEach((column) => {
        if (column) {
          const columnItemElements = column.querySelectorAll('[data-item-key]');
          columnItemElements.forEach((item) => {
            const key = (item as HTMLElement).dataset.itemKey;
            if (key) currentKeys.add(key);
          });
        }
      });

      // 移除不再存在的元素的观察
      observedElementsRef.current.forEach((element) => {
        const key = element.dataset.itemKey;
        if (key && !currentKeys.has(key)) {
          observer.unobserve(element);
          observedElementsRef.current.delete(element);
        }
      });

      // 观察新元素
      columnsRef.current.forEach((column) => {
        if (column) {
          const columnItemElements = column.querySelectorAll('[data-item-key]');
          columnItemElements.forEach((item) => {
            const element = item as HTMLElement;
            const key = element.dataset.itemKey;

            // 只观察尚未观察的元素
            if (key && !observedElementsRef.current.has(element)) {
              // 如果高度还未缓存，先尝试获取一次
              if (!itemHeightsRef.current.has(key) && element.offsetHeight > 0) {
                itemHeightsRef.current.set(key, element.offsetHeight);
              }
              observer.observe(element);
              observedElementsRef.current.add(element);
              newElements.push(element);
            }
          });
        }
      });

      // 如果有新元素被观察且高度已缓存，可能需要重新分配
      if (newElements.length > 0) {
        // 检查是否有新元素的高度被缓存了
        const hasNewHeight = newElements.some((element) => {
          const key = element.dataset.itemKey;
          return key && itemHeightsRef.current.has(key);
        });

        if (hasNewHeight) {
          // 延迟执行，等待所有元素渲染完成
          setTimeout(() => {
            if (distributeItemsRef.current) {
              distributeItemsRef.current();
            }
          }, 50);
        }
      }
    }, 150); // 增加延迟，确保 DOM 渲染完成

    return () => {
      clearTimeout(timeoutId);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
      // 注意：不在这里 disconnect observer，因为它是复用的
    };
  }, [items.length, columns, virtualized]); // 移除 distributeItems 依赖，使用 ref 访问

  // 处理窗口大小变化（防抖）
  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    let timer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (containerRef.current) {
          const width = containerRef.current.clientWidth;
          const newColumns = calculateColumns(width);
          setColumns((prevColumns) => {
            // 使用函数式更新，避免依赖 columns
            if (newColumns !== prevColumns) {
              return newColumns;
            }
            return prevColumns;
          });
        }
      }, 200); // 增加防抖时间
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [calculateColumns]); // 移除 columns 依赖

  // 渲染列内容
  const renderColumn = useCallback((columnItemsInColumn: ColumnItem<T>[], columnIndex: number) => {
    return (
      <div
        key={columnIndex}
        ref={(el) => {
          if (el) columnsRef.current[columnIndex] = el;
        }}
        className={styles.column}
        style={{
          width: `calc((100% - ${columnGap * (columns - 1)}px) / ${columns})`,
          marginRight: columnIndex < columns - 1 ? `${columnGap}px` : '0',
        }}
      >
        {columnItemsInColumn.map(({ item, index }) => {
          const key = getItemKey(item, index);
          return (
            <div
              key={key}
              data-item-key={String(key)}
              className={styles.item}
              style={{ marginBottom: `${rowGap}px` }}
            >
              {renderItem(item, index)}
            </div>
          );
        })}
      </div>
    );
  }, [columns, columnGap, rowGap, renderItem, getItemKey]);

  const renderedItems = columnItems.flat();
  const hasCurrentItems = renderedItems.length === items.length
    && items.every((item) => renderedItems.some((columnItem) => columnItem.item === item));
  let visibleColumnItems = columnItems;
  if (!hasCurrentItems) {
    visibleColumnItems = [];
    if (items.length > 0) {
      visibleColumnItems = [items.map((item, index) => ({ item, index, height: 0 }))];
    }
  }

  return (
    <div
      ref={containerRef}
      className={classNames(styles.waterfall, className)}
      style={style}
    >
      {visibleColumnItems.map((columnItemsInColumn, columnIndex) => renderColumn(columnItemsInColumn, columnIndex))}
    </div>
  );
}

export default Waterfall;
